import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

interface Deficiency {
  _id: string;
  deficiencyNumber: number;
  title: string;
  status: string;
  priority: string;
  unit?: {
    unitNo: string;
  };
  commonArea?: {
    areaName: string;
  };
}

interface RelatedDeficiencyItem {
  deficiency: string;
  relationshipType: 'duplicate' | 'related';
  deficiencyNumber?: number;
  title?: string;
}

interface RelatedDeficiencyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedDeficiencies: RelatedDeficiencyItem[]) => void;
  workspaceId: string;
  projectId: string;
  unitId?: string;
  commonAreaId?: string;
  existingRelatedIds?: string[];
  currentDeficiencyId?: string;
}

const RelatedDeficiencyModal: React.FC<RelatedDeficiencyModalProps> = ({
  isOpen,
  onClose,
  onSave,
  workspaceId,
  projectId,
  unitId,
  commonAreaId,
  existingRelatedIds = [],
  currentDeficiencyId
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [deficiencies, setDeficiencies] = useState<Deficiency[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDeficiencies, setSelectedDeficiencies] = useState<string[]>([]);
  const [relationshipTypes, setRelationshipTypes] = useState<{ [key: string]: 'duplicate' | 'related' }>({});
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch deficiencies when modal opens or search query changes
  useEffect(() => {
    if (isOpen && projectId) {
      // Clear existing timeout
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      // Set new timeout - wait 300ms after user stops typing
      searchDebounceRef.current = setTimeout(() => {
        fetchDeficiencies();
      }, 300);

      return () => {
        if (searchDebounceRef.current) {
          clearTimeout(searchDebounceRef.current);
        }
      };
    }
  }, [isOpen, searchQuery, projectId, unitId, commonAreaId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setDeficiencies([]);
      setSelectedDeficiencies([]);
      setRelationshipTypes({});
    }
  }, [isOpen]);

  const fetchDeficiencies = async () => {
    if (!workspaceId || !projectId) return;

    try {
      setLoading(true);
      const params: any = {
        page: 1,
        limit: 50,
        sortBy: 'deficiencyNumber',
        sortOrder: 'desc'
      };

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      // Filter by unitId or commonAreaId based on scope
      if (unitId) {
        params.unitId = unitId;
      } else if (commonAreaId) {
        params.commonAreaId = commonAreaId;
      }

      const response = await api.get(`/workspaces/${workspaceId}/${projectId}/deficiencies`, { params });

      // Filter out already related deficiencies and the current deficiency
      const filteredDeficiencies = (response.data.deficiencies || []).filter(
        (def: Deficiency) => !existingRelatedIds.includes(def._id) && def._id !== currentDeficiencyId
      );

      setDeficiencies(filteredDeficiencies);
    } catch (err: any) {
      console.error('Error fetching deficiencies:', err);
      toast.error('Failed to load deficiencies');
      setDeficiencies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDeficiency = (deficiencyId: string) => {
    setSelectedDeficiencies(prev => {
      if (prev.includes(deficiencyId)) {
        // Remove from selected
        const newSelected = prev.filter(id => id !== deficiencyId);
        // Remove relationship type
        const newTypes = { ...relationshipTypes };
        delete newTypes[deficiencyId];
        setRelationshipTypes(newTypes);
        return newSelected;
      } else {
        // Add to selected with default relationship type
        setRelationshipTypes(prev => ({
          ...prev,
          [deficiencyId]: 'related'
        }));
        return [...prev, deficiencyId];
      }
    });
  };

  const handleRelationshipTypeChange = (deficiencyId: string, type: 'duplicate' | 'related') => {
    setRelationshipTypes(prev => ({
      ...prev,
      [deficiencyId]: type
    }));
  };

  const handleSave = () => {
    if (selectedDeficiencies.length === 0) {
      toast.warning('Please select at least one deficiency');
      return;
    }

    // Build array of related deficiency items with their relationship types and details
    const relatedItems: RelatedDeficiencyItem[] = selectedDeficiencies.map(deficiencyId => {
      const deficiencyData = deficiencies.find(def => def._id === deficiencyId);
      return {
        deficiency: deficiencyId,
        relationshipType: relationshipTypes[deficiencyId] || 'related',
        deficiencyNumber: deficiencyData?.deficiencyNumber,
        title: deficiencyData?.title
      };
    });

    onSave(relatedItems);
    onClose();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onClose}
      >
        {/* Modal Container */}
        <div
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#48182F', margin: 0 }}>
              Add Related Deficiency
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6B7280',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#48182F'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
            >
              <X size={24} />
            </button>
          </div>

          {/* Search Section */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #E5E7EB' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#48182F', marginBottom: '8px', display: 'block' }}>
              Search for deficiencies :
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by title.."
                value={searchQuery}
                onChange={handleSearchChange}
                className="form-input"
                style={{
                  width: '100%',
                  paddingRight: searchQuery ? '40px' : '12px',
                  backgroundColor: '#F5F3F0',
                  border: '1px solid #D4CBC4'
                }}
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9CA3AF',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#48182F'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Selected Items Count */}
          <div style={{
            padding: '12px 24px',
            backgroundColor: '#F9FAFB',
            borderBottom: '1px solid #E5E7EB',
            fontSize: '14px',
            fontWeight: '500',
            color: '#48182F'
          }}>
            Selected Items : {selectedDeficiencies.length}
          </div>

          {/* Deficiencies List */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px 24px'
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                Loading deficiencies...
              </div>
            ) : deficiencies.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
                No deficiencies found
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {deficiencies.map((deficiency) => {
                  const isSelected = selectedDeficiencies.includes(deficiency._id);
                  const relationshipType = relationshipTypes[deficiency._id] || 'related';

                  return (
                    <div
                      key={deficiency._id}
                      style={{
                        padding: '16px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        backgroundColor: isSelected ? '#F5F3F0' : '#FFFFFF',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleDeficiency(deficiency._id)}
                          style={{
                            marginTop: '4px',
                            cursor: 'pointer',
                            width: '18px',
                            height: '18px'
                          }}
                        />

                        {/* Deficiency Info */}
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#48182F',
                            marginBottom: '4px'
                          }}>
                            {deficiency.title}
                          </div>
                          <div style={{
                            fontSize: '13px',
                            color: '#6B7280',
                            marginBottom: '8px'
                          }}>
                            #{deficiency.deficiencyNumber} • {deficiency.unit?.unitNo || deficiency.commonArea?.areaName || 'N/A'}
                          </div>

                          {/* Relationship Type Radio Buttons (only shown when selected) */}
                          {isSelected && (
                            <div style={{
                              display: 'flex',
                              gap: '24px',
                              marginTop: '12px',
                              paddingTop: '12px',
                              borderTop: '1px solid #E5E7EB'
                            }}>
                              <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#48182F'
                              }}>
                                <input
                                  type="radio"
                                  name={`relationship-${deficiency._id}`}
                                  value="duplicate"
                                  checked={relationshipType === 'duplicate'}
                                  onChange={() => handleRelationshipTypeChange(deficiency._id, 'duplicate')}
                                  style={{ cursor: 'pointer' }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span>Duplicate</span>
                              </label>
                              <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                color: '#48182F'
                              }}>
                                <input
                                  type="radio"
                                  name={`relationship-${deficiency._id}`}
                                  value="related"
                                  checked={relationshipType === 'related'}
                                  onChange={() => handleRelationshipTypeChange(deficiency._id, 'related')}
                                  style={{ cursor: 'pointer' }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <span>Related</span>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}>
            <button
              onClick={onClose}
              className="btn btn-secondary btn-md"
            >
              CANCEL
            </button>
            <button
              onClick={handleSave}
              className="btn btn-primary btn-md"
              style={{
                backgroundColor: '#48182F',
                border: 'none',
                color: '#FFFFFF'
              }}
            >
              SAVE
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RelatedDeficiencyModal;
